import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import SortableSlots from "../components/SortableSlots";
import { Scale, Lock, Plus, X } from "lucide-react";

// Pattern categories the Advanced lifter can pick from when adding an extra
// exercise. Must include EVERY pattern that exists in the seeded exercise
// library — otherwise that pattern's exercises become invisible to extras
// (the bug fixed in 2026-02-22 was rear_delt_fly + shrug being absent).
// Aligned 1:1 with the `pattern` field on the exercise documents so the
// back-end filter is "give me everything with this pattern". Order here is
// the order rendered in the picker — biggest compounds first, then
// isolations / accessories.
const EXTRA_PATTERN_OPTIONS = [
    { value: "horizontal_push", label: "Chest · Horizontal Press" },
    { value: "vertical_push", label: "Shoulders · Overhead Press" },
    { value: "horizontal_pull", label: "Back · Horizontal Row" },
    { value: "vertical_pull", label: "Back · Vertical Pull" },
    { value: "shrug", label: "Traps · Shrug" },
    { value: "squat", label: "Legs · Squat" },
    { value: "hip_hinge", label: "Legs · Hinge" },
    { value: "knee_extension", label: "Legs · Knee Extension" },
    { value: "knee_flexion", label: "Legs · Hamstring (Knee Flexion)" },
    { value: "hip_extension", label: "Glutes / Posterior Chain" },
    { value: "hip_abduction", label: "Glute Med · Abductor" },
    { value: "hip_adduction", label: "Adductors" },
    { value: "lateral_raise", label: "Shoulders · Lateral" },
    { value: "rear_delt_fly", label: "Shoulders · Rear Delt Fly" },
    { value: "elbow_flexion", label: "Biceps · Curl" },
    { value: "elbow_extension", label: "Triceps · Extension" },
    { value: "ankle_extension", label: "Calves" },
    { value: "trunk_flexion", label: "Core · Trunk Flexion" },
];
const MAX_EXTRAS = 2;

// Build the ordered item list for a given day. Each item carries everything
// `SortableSlots` needs to render and everything `startSession` needs to
// build the workout log. Slot items and Extra items share the same shape so
// they can be reordered freely against each other.
function itemsFromDay(day) {
    return day.slots.map((s, i) => {
        const opts = s.options || [];
        const explicit = s.default_exercise_id ? opts.find((o) => o.id === s.default_exercise_id) : null;
        const primary = explicit || opts.find((o) => o.primary) || opts[0];
        return {
            id: `slot-${i}-${s.label}`,
            kind: "slot",
            label: s.label,
            unilateral: !!s.unilateral,
            options: opts,
            selectedId: primary?.id || "",
        };
    });
}

export default function Workouts() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [program, setProgram] = useState(null);
    const [selectedDay, setSelectedDay] = useState(0);
    // Single ordered list of items, holding both program slots and
    // user-added extras. SortableSlots reorders this array directly.
    // Rebuilt whenever the day tab changes (extras don't survive day switches).
    const [items, setItems] = useState([]);
    const [patternCache, setPatternCache] = useState({}); // pattern -> exercises[]
    // Full exercise library, lazy-loaded once when an advanced user first
    // unlocks "any exercise" on a slot. Cached in-page for the session.
    const [allExercises, setAllExercises] = useState(null);
    // Track which slot ids are unlocked (user chose "Any exercise"). Local to
    // the current day view — cleared on day switch alongside the items array.
    const [unlockedSlots, setUnlockedSlots] = useState({});

    const loadAllExercises = async () => {
        if (allExercises) return allExercises;
        try {
            const { data } = await api.get(`/exercises`);
            setAllExercises(data);
            return data;
        } catch { return []; }
    };

    const toggleSlotUnlock = async (id) => {
        setUnlockedSlots((prev) => ({ ...prev, [id]: !prev[id] }));
        if (!allExercises) await loadAllExercises();
    };

    useEffect(() => {
        api.get(`/programs/${user.split || "upper_lower"}`).then((r) => {
            setProgram(r.data);
            setItems(itemsFromDay(r.data.days[0]));
        });
    }, [user.split]);

    const changeDay = (idx) => {
        setSelectedDay(idx);
        setItems(itemsFromDay(program.days[idx]));
    };

    // Lazy-load and cache the exercise list for a given pattern. The backend
    // `/api/exercises?pattern=` endpoint returns every exercise tagged with
    // that pattern (primary + alternatives).
    const loadPatternOptions = async (pattern) => {
        if (patternCache[pattern]) return patternCache[pattern];
        try {
            const { data } = await api.get(`/exercises`, { params: { pattern } });
            setPatternCache((c) => ({ ...c, [pattern]: data }));
            return data;
        } catch (e) {
            return [];
        }
    };

    const extrasCount = items.filter((it) => it.kind === "extra").length;

    const updateItem = (id, patch) => {
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    };

    const addExtra = async () => {
        if (extrasCount >= MAX_EXTRAS) return;
        // Default to the first pattern the user hasn't picked yet — small UX
        // nudge so they don't accidentally double-up the same category.
        const used = new Set(items.filter((i) => i.kind === "extra").map((i) => i.pattern));
        const defaultPattern = (EXTRA_PATTERN_OPTIONS.find((p) => !used.has(p.value)) || EXTRA_PATTERN_OPTIONS[0]).value;
        const options = await loadPatternOptions(defaultPattern);
        const firstOpt = options.find((o) => o.primary) || options[0];
        const id = `extra-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setItems((prev) => [
            ...prev,
            {
                id,
                kind: "extra",
                pattern: defaultPattern,
                options,
                selectedId: firstOpt?.id || "",
            },
        ]);
    };

    const updateExtraPattern = async (id, newPattern) => {
        const options = await loadPatternOptions(newPattern);
        const firstOpt = options.find((o) => o.primary) || options[0];
        updateItem(id, { pattern: newPattern, options, selectedId: firstOpt?.id || "" });
    };

    const removeExtra = (id) => {
        setItems((prev) => prev.filter((it) => it.id !== id));
    };

    const startSession = async () => {
        const day = program.days[selectedDay];
        // Advanced users can pick from the full 60+ exercise library when a
        // slot is "unlocked" — meaning the selected exercise may live outside
        // that slot's pattern-restricted `options` array. If we ONLY search
        // item.options here, the swapped-in exercise vanishes (find returns
        // undefined, .filter(Boolean) drops the whole slot) and every slot
        // below it shifts up by one — which is the "wrong order" bug reported
        // 2026-07 (Lower A · hip_thrust swapped in for abductor). Fix: also
        // consult allExercises as a fallback so the reorder + swap combo
        // survives the session build.
        const exercises = items
            .map((item) => {
                const ex = (item.options || []).find((o) => o.id === item.selectedId)
                        || (allExercises || []).find((o) => o.id === item.selectedId);
                if (!ex) return null;
                const setCount = ex.is_compound ? 3 : 2;
                const sets = Array.from({ length: setCount }, (_, i) => ({
                    set_number: i + 1,
                    reps: 0,
                    weight: 0,
                    rir: 1,
                }));
                return {
                    exercise_id: ex.id,
                    exercise_name: ex.name,
                    slot_label: item.kind === "extra" ? `Extra · ${ex.name}` : item.label,
                    is_compound: !!ex.is_compound,
                    optional: !!ex.optional,
                    sets,
                };
            })
            .filter(Boolean);
        try {
            const { data } = await api.post("/workouts", {
                program_key: program.key,
                day_name: day.name,
                exercises,
            });
            toast.success("Session started");
            navigate(`/session/${data.log_id}`);
        } catch (e) {
            toast.error("Failed to start session");
        }
    };

    if (!program) return <div data-testid="workouts-loading">Loading…</div>;

    const day = program.days[selectedDay];
    // Reorder is a power-user feature — only Advanced lifters who actually know
    // which muscle is lagging should be moving things around. Beginners and
    // intermediates stick to the curated default order which is already
    // sequenced for warm-up flow, fatigue distribution, and joint priming.
    const canReorder = user?.experience === "advanced";

    return (
        <div className="space-y-8" data-testid="workouts-page">
            <div>
                <div className="label-eyebrow text-accent-red">{program.name}</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">Program</h1>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{program.description}</p>
                <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground whitespace-pre-line">Schedule:{"\n"}{program.schedule}</p>
            </div>

            <div className="flex flex-wrap gap-2">
                {program.days.map((d, i) => (
                    <button key={d.key} onClick={() => changeDay(i)} data-testid={`day-tab-${d.key}`}
                        className={`border px-4 py-2 text-xs font-bold uppercase tracking-wider ${i === selectedDay ? "border-accent-red bg-accent-red text-white" : "border-border bg-card hover:bg-secondary"}`}>
                        {d.name}
                    </button>
                ))}
            </div>

            {canReorder ? (
                <div className="rounded-sm border border-accent-red/40 bg-accent-red/5 p-3 text-xs text-foreground/85" data-testid="workouts-dnd-note">
                    <span className="font-bold uppercase tracking-wider text-accent-red">Tap &amp; hold any slot to reorder.</span>{" "}
                    Note: the default exercise selection and slot order is already set up for general efficiency (warm-up flow, fatigue distribution, joint priming). Only change it if you&apos;re deliberately attacking a lagging muscle while you&apos;re freshest. Reordering applies to <span className="font-bold">this session only</span> when you hit Start — the tab resets to default afterward.
                </div>
            ) : (
                <div className="flex items-start gap-2 rounded-sm border border-border bg-card/60 p-3 text-xs text-foreground/85" data-testid="workouts-dnd-locked">
                    <Lock className="mt-0.5 h-3.5 w-3.5 flex-none text-muted-foreground" />
                    <p>
                        <span className="font-bold uppercase tracking-wider">Slot order locked.</span>{" "}
                        Reordering is an Advanced-only feature — the default exercise selection and slot order is already set up for general efficiency. Stick to the program as written until you&apos;ve built the strength base and self-awareness to know what your weak link actually is.
                    </p>
                </div>
            )}

            {canReorder && extrasCount > 0 && (
                <div className="rounded-sm border border-amber-400/40 bg-amber-400/5 p-3 text-xs text-foreground/85" data-testid="extras-warning">
                    <span className="font-bold uppercase tracking-wider text-amber-400">Extras add fatigue.</span>{" "}
                    Use these to attack a lagging muscle — not for general &ldquo;more is better.&rdquo; The 4–6 hard sets/muscle/week rule still applies.
                </div>
            )}

            <SortableSlots
                items={items}
                onReorder={canReorder ? (reordered) => setItems(reordered) : undefined}
                disabled={!canReorder}
                renderItem={(item, idx) => {
                    const selectedOpt = (item.options || []).find((o) => o.id === item.selectedId);
                    const setDefault = item.kind === "extra"
                        ? (selectedOpt?.is_compound ? "3 working sets default" : "2 working sets default")
                        : (program.key === "full_body"
                            ? "2 working sets default"
                            : (selectedOpt?.is_compound ? "3 working sets default" : "2 working sets default"));
                    if (item.kind === "extra") {
                        return (
                            <div className="flex flex-col gap-3" data-testid={`extra-row-${item.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="label-eyebrow text-accent-red">Extra exercise</div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-display text-lg font-black uppercase leading-tight sm:text-xl">{selectedOpt?.name || "Pick one"}</span>
                                            {selectedOpt?.unilateral && (
                                                <span className="flex items-center gap-1 rounded-sm border border-accent-red/60 px-1.5 py-0.5 text-[10px] tracking-wider text-accent-red">
                                                    <Scale className="h-2.5 w-2.5" /> UNI
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeExtra(item.id)}
                                        data-testid={`extra-remove-${item.id}`}
                                        aria-label="Remove extra"
                                        onPointerDown={(e) => e.stopPropagation()}
                                        className="rounded-sm border border-border bg-card/60 p-1 text-white/55 hover:border-accent-red hover:text-accent-red"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    <select
                                        data-testid={`extra-pattern-${item.id}`}
                                        value={item.pattern}
                                        onChange={(e) => updateExtraPattern(item.id, e.target.value)}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        className="w-full max-w-full truncate rounded-sm border border-border bg-card px-3 py-2 text-sm"
                                    >
                                        {EXTRA_PATTERN_OPTIONS.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                    <select
                                        data-testid={`extra-exercise-${item.id}`}
                                        value={item.selectedId}
                                        onChange={(e) => updateItem(item.id, { selectedId: e.target.value })}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        className="w-full max-w-full truncate rounded-sm border border-border bg-card px-3 py-2 text-sm"
                                    >
                                        {(item.options || []).length === 0 && <option value="">No exercises found</option>}
                                        {(item.options || []).map((o) => {
                                            const flags = [];
                                            if (o.primary) flags.push("★");
                                            if (o.unilateral) flags.push("UNI");
                                            if (o.optional) flags.push("OPT");
                                            const prefix = flags.length ? `${flags.join(" ")} · ` : "";
                                            return <option key={o.id} value={o.id}>{prefix}{o.name}</option>;
                                        })}
                                    </select>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {setDefault} · 4–8 reps · 1–2 RIR · max 4 sets · session-only
                                </p>
                            </div>
                        );
                    }
                    const unlocked = !!unlockedSlots[item.id];
                    const slotOptions = unlocked && allExercises ? allExercises : (item.options || []);
                    return (
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="label-eyebrow text-muted-foreground">Slot {idx + 1}</div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-display text-lg font-black uppercase leading-tight sm:text-xl">{item.label}</span>
                                        {item.unilateral && (
                                            <span className="flex items-center gap-1 rounded-sm border border-accent-red/60 px-1.5 py-0.5 text-[10px] tracking-wider text-accent-red">
                                                <Scale className="h-2.5 w-2.5" /> UNI
                                            </span>
                                        )}
                                        {unlocked && (
                                            <span data-testid={`slot-unlocked-badge-${idx}`} className="rounded-sm border border-accent-red/60 bg-accent-red/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent-red">
                                                Full library
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {canReorder && (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); toggleSlotUnlock(item.id); }}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        data-testid={`slot-advanced-toggle-${idx}`}
                                        aria-pressed={unlocked}
                                        className={`flex-none rounded-sm border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${unlocked ? "border-accent-red bg-accent-red/15 text-accent-red" : "border-border bg-card/60 text-white/70 hover:border-accent-red/60 hover:text-accent-red"}`}
                                    >
                                        {unlocked ? "Restrict to pattern" : "Show all exercises"}
                                    </button>
                                )}
                            </div>
                            <select
                                data-testid={`slot-select-${idx}`}
                                value={item.selectedId || ""}
                                onChange={(e) => updateItem(item.id, { selectedId: e.target.value })}
                                onPointerDown={(e) => e.stopPropagation()}
                                className="w-full max-w-full truncate rounded-sm border border-border bg-card px-3 py-2 text-sm"
                            >
                                {slotOptions.map((o) => {
                                    const flags = [];
                                    if (o.primary) flags.push("★");
                                    if (o.unilateral) flags.push("UNI");
                                    if (o.optional) flags.push("OPT");
                                    const prefix = flags.length ? `${flags.join(" ")} · ` : "";
                                    return <option key={o.id} value={o.id}>{prefix}{o.name}</option>;
                                })}
                            </select>
                            <p className="text-xs text-muted-foreground">
                                {setDefault} · 4–8 reps · 1–2 RIR · max 4 sets · {unlocked ? "any exercise · advanced mode" : "double progression"}
                            </p>
                        </div>
                    );
                }}
            />

            {canReorder && (
                extrasCount < MAX_EXTRAS ? (
                    <button
                        onClick={addExtra}
                        data-testid="extras-add-btn"
                        className="flex w-full items-center justify-center gap-2 rounded-sm border border-dashed border-accent-red/60 bg-accent-red/5 px-4 py-3 text-xs font-bold uppercase tracking-wider text-accent-red hover:bg-accent-red/10 sm:w-auto"
                    >
                        <Plus className="h-4 w-4" /> Add exercise <span className="text-white/60">({extrasCount}/{MAX_EXTRAS})</span>
                    </button>
                ) : (
                    <div className="rounded-sm border border-border bg-card/60 px-4 py-3 text-xs text-muted-foreground" data-testid="extras-cap">
                        <span className="font-bold uppercase tracking-wider text-foreground/80">Volume cap reached.</span>{" "}
                        Two extras max per session — more is not better. Recovery is where growth happens.
                    </div>
                )
            )}

            <button onClick={startSession} data-testid="start-session-btn" className="w-full rounded-sm bg-accent-red px-6 py-4 text-sm font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] sm:w-auto">
                Start {day.name}
            </button>
        </div>
    );
}
